<script setup>
import AppLayout from '@/Layouts/AppLayout.vue'
import PageShell from '@/Components/UI/PageShell.vue'
import UiCard from '@/Components/UI/UiCard.vue'
import UiButton from '@/Components/UI/UiButton.vue'
import SettlePayoutModal from '@/Components/SettlePayoutModal.vue'
import { Link, usePage } from '@inertiajs/vue3'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useCurrency } from '@/composables/useCurrency.js'

defineOptions({ layout: AppLayout })
const props = defineProps({
  driver:        { type: Object, required: true },
  temp_password: { type: String, default: null },
})

// One-time-render banner — shown after Create/Reset/Invite. We snapshot
// the value on mount so it survives prop reactivity if the parent ever
// re-renders without the flash payload.
const showTempPassword = ref(!!props.temp_password)
const tempPasswordValue = props.temp_password

const { t } = useI18n()
const { formatMoney, formatDate } = useCurrency()
const page = usePage()
const isAdmin = computed(() => page.props.auth?.user?.role === 'admin')
const showSettle = ref(false)
</script>

<template>
  <PageShell
    :title="driver.name"
    back-href="/drivers"
  >
    <template #actions>
      <Link
        :href="`/drivers/${driver.id}/edit`"
        class="btn"
      >
        {{ t('drivers.edit_action') }}
      </Link>
      <Link
        v-if="isAdmin && !driver.archived_at"
        :href="`/drivers/${driver.id}/archive`"
        method="patch"
        as="button"
        class="btn btn--danger"
      >
        {{ t('drivers.archive') }}
      </Link>
    </template>

    <!-- Earnings card -->
    <UiCard
      v-if="driver.outstanding_cents !== undefined"
      :title="t('drivers.show.earnings.title')"
    >
      <dl class="grid grid-cols-2 gap-3 text-sm">
        <div>
          <dt class="text-[color:var(--muted)]">{{ t('drivers.show.earnings.outstanding') }}</dt>
          <dd class="fw-value tabular-nums mt-0.5" :style="{ color: driver.outstanding_cents > 0 ? 'var(--warn)' : 'var(--ink)' }">
            {{ formatMoney(driver.outstanding_cents) }}
            <span v-if="driver.outstanding_count" class="text-xs ml-1" style="color: var(--muted);">
              ({{ driver.outstanding_count }})
            </span>
          </dd>
        </div>
      </dl>
      <UiButton
        v-if="isAdmin && driver.outstanding_cents > 0 && driver.unpaid_deliveries && driver.unpaid_deliveries.length"
        variant="primary"
        class="mt-4"
        @click="showSettle = true"
      >
        {{ t('drivers.show.earnings.settleNow') }}
      </UiButton>
      <SettlePayoutModal
        v-if="showSettle"
        :driver-id="driver.id"
        :deliveries="driver.unpaid_deliveries"
        @close="showSettle = false"
      />
    </UiCard>

    <!-- Past payouts -->
    <UiCard
      v-if="driver.recent_payouts && driver.recent_payouts.length"
      :title="t('drivers.show.earnings.pastPayouts')"
    >
      <ul class="divide-y" style="border-color: var(--line);">
        <li
          v-for="p in driver.recent_payouts"
          :key="p.id"
        >
          <Link
            :href="`/drivers/${driver.id}/payouts/${p.id}`"
            class="flex justify-between py-2 px-2 -mx-2 rounded text-sm"
            style="text-decoration: none; color: inherit;"
          >
            <span style="color: var(--inkSoft);">
              {{ formatDate(p.paid_at) }} · {{ p.method }} · {{ p.delivery_count }} {{ t('drivers.show.earnings.deliveriesShort') }}
            </span>
            <span class="fw-value tabular-nums" style="color: var(--ink);">{{ p.amount }}</span>
          </Link>
        </li>
      </ul>
    </UiCard>

    <section v-if="showTempPassword" class="card temp-pw-card">
      <div class="temp-pw-header">
        <h2>{{ t('drivers.temp_password.heading') }}</h2>
        <button type="button" class="temp-pw-dismiss" @click="showTempPassword = false">×</button>
      </div>
      <p class="temp-pw-body">{{ t('drivers.temp_password.body') }}</p>
      <code class="temp-pw-value">{{ tempPasswordValue }}</code>
      <p class="temp-pw-hint">{{ t('drivers.temp_password.hint') }}</p>
    </section>

    <section class="card">
      <h2>{{ t('drivers.sections.identity') }}</h2>
      <div class="id-grid">
        <img
          v-if="driver.photo_url"
          :src="driver.photo_url"
          alt="Driver photo"
          class="photo"
        >
        <div
          v-else
          class="photo placeholder"
        >
          {{ driver.name?.[0] }}
        </div>
        <dl class="kv">
          <dt>{{ t('drivers.fields.name') }}</dt>
          <dd>{{ driver.name }}</dd>
          <dt>{{ t('drivers.fields.phone') }}</dt>
          <dd>{{ driver.phone || '—' }}</dd>
          <dt>{{ t('drivers.fields.license_number') }}</dt>
          <dd>{{ driver.license_number || '—' }}</dd>
          <dt>{{ t('drivers.fields.license_expiry') }}</dt>
          <dd>
            {{ driver.license_expiry || '—' }}
            <span
              v-if="driver.license_status === 'expired'"
              class="pill pill--danger"
            >{{ t('drivers.license.expired') }}</span>
            <span
              v-else-if="driver.license_status === 'expiring_soon'"
              class="pill pill--warn"
            >{{ t('drivers.license.expiring_soon') }}</span>
          </dd>
          <template v-if="driver.notes">
            <dt>{{ t('drivers.fields.notes') }}</dt>
            <dd>{{ driver.notes }}</dd>
          </template>
        </dl>
      </div>
    </section>

    <section class="card">
      <h2>{{ t('drivers.sections.channels') }}</h2>
      <ul
        v-if="driver.contact_channels?.length"
        class="channels"
      >
        <li
          v-for="c in driver.contact_channels"
          :key="c.id"
        >
          <span class="ch-platform">{{ c.platform }}</span> · {{ c.handle }}
        </li>
      </ul>
      <p
        v-else
        class="muted"
      >
        {{ t('drivers.no_channels') }}
      </p>
    </section>

    <section class="card">
      <h2>{{ t('drivers.sections.portal') }}</h2>
      <p v-if="driver.portal_account">
        {{ driver.portal_account.email }} ·
        <span :class="['pill', driver.portal_account.status === 'active' ? 'pill--active' : 'pill--disabled']">
          {{ driver.portal_account.status }}
        </span>
        <span
          v-if="driver.portal_account.last_sign_in_at"
          class="muted"
        > · {{ t('drivers.portal.last_sign_in') }} {{ driver.portal_account.last_sign_in_at }}</span>
        <Link
          v-if="isAdmin"
          :href="`/admin/portal_accounts/${driver.portal_account.id}`"
          class="link"
        >
          {{ t('drivers.portal.manage') }} →
        </Link>
      </p>
      <p v-else>
        {{ t('drivers.portal.none') }}
        <Link
          v-if="isAdmin"
          :href="`/admin/portal_accounts/new?owner_type=Driver&owner_id=${driver.id}`"
          class="link"
        >
          + {{ t('drivers.portal.provision') }}
        </Link>
      </p>
    </section>
  </PageShell>
</template>

<style scoped>
.card { background: var(--surface); border: 1px solid var(--line); border-radius: 0.75rem; padding: 1.25rem; margin-bottom: 1rem; }
.card h2 { font-size: 0.875rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--muted); margin: 0 0 0.75rem; }
.temp-pw-card { background: color-mix(in oklch, var(--warn) 14%, var(--surface)); border-color: color-mix(in oklch, var(--warn) 40%, var(--line)); }
.temp-pw-header { display: flex; justify-content: space-between; align-items: center; }
.temp-pw-header h2 { margin: 0; color: var(--warn); }
.temp-pw-dismiss { background: transparent; border: 0; font-size: 1.25rem; cursor: pointer; color: var(--muted); padding: 0 0.4rem; }
.temp-pw-body { margin: 0.4rem 0 0.5rem; color: var(--ink); font-size: 0.9375rem; }
.temp-pw-value { display: inline-block; padding: 0.4rem 0.7rem; background: var(--surface); border: 1px solid var(--line); border-radius: var(--radius-input, 6px); font-family: var(--font-mono, monospace); font-size: 1rem; user-select: all; }
.temp-pw-hint { margin: 0.4rem 0 0; font-size: 0.8125rem; color: var(--muted); }
.id-grid { display: grid; grid-template-columns: 6rem 1fr; gap: 1rem; align-items: start; }
.photo { width: 6rem; height: 6rem; object-fit: cover; border-radius: 0.5rem; background: var(--surfaceAlt); }
.placeholder { display: grid; place-items: center; font-size: 2rem; font-weight: 700; color: var(--muted); background: var(--surfaceAlt); }
.kv { display: grid; grid-template-columns: 10rem 1fr; gap: 0.4rem 1rem; margin: 0; }
.kv dt { color: var(--muted); font-size: 0.875rem; }
.kv dd { margin: 0; color: var(--ink); font-size: 0.875rem; }
.channels { list-style: none; padding: 0; margin: 0; }
.channels li { padding: 0.3rem 0; font-size: 0.875rem; color: var(--ink); border-bottom: 1px solid var(--line); }
.channels li:last-child { border-bottom: none; }
.ch-platform { text-transform: capitalize; color: var(--inkSoft); }
.muted { color: var(--muted); margin-left: 0.5rem; font-size: 0.875rem; }
.link { color: var(--accent); text-decoration: none; margin-left: 0.5rem; }
.pill { display: inline-block; padding: 0.15rem 0.5rem; border-radius: 999px; font-size: 0.75rem; }
.pill--danger   { background: color-mix(in oklch, var(--danger) 14%, var(--surface)); color: var(--danger); }
.pill--warn     { background: color-mix(in oklch, var(--warn) 14%, var(--surface)); color: var(--warn); }
.pill--active   { background: color-mix(in oklch, var(--ok) 16%, var(--surface)); color: var(--ok); }
.pill--disabled { background: color-mix(in oklch, var(--muted) 14%, var(--surface)); color: var(--muted); }
.btn { padding: 0.4rem 0.85rem; border: 1px solid var(--line); background: var(--surface); color: var(--ink); border-radius: var(--radius-button, 8px); text-decoration: none; font-weight: 500; display: inline-block; cursor: pointer; font: inherit; }
.btn--danger { color: var(--danger); border-color: color-mix(in oklch, var(--danger) 40%, var(--line)); }
</style>
